/**
 * The design compiler pipeline.
 *
 * propose → validate → repair → re-validate → candidate.
 *
 * The invariant that makes the whole architecture safe: **nothing leaves this
 * pipeline unvalidated.** A generator — deterministic or model-backed — is
 * untrusted output. It is checked against the closed vocabulary, and a spec
 * that cannot be made valid is refused rather than shipped.
 */

import { validateSpec, type TLayoutSpec, type Problem } from '@morph/spec';
import type { GenerationRequest, GenerationOutcome, Generator } from './types.js';
import { AcceptedNoLongerLegal } from './revise.js';
import { TemplateUnsatisfiable, templateShortfalls } from './app-template.js';

export interface CompileOptions {
  /** How many repair rounds before giving up. Repair is deterministic, so more
   *  than a couple of rounds means the generator is producing something we
   *  fundamentally cannot fix. */
  maxAttempts?: number;
  fallback?: Generator;
}

/**
 * Deterministic repair of the failures we know how to fix.
 *
 * Deliberately narrow. Repair exists to absorb the small, mechanical mistakes a
 * generator makes — a write binding missing its mutation, a filter reading an
 * undeclared slot — not to rescue a fundamentally wrong design. Broadening it
 * would let a bad generator look good, which is the opposite of what we want
 * from the floor this pipeline enforces.
 */
export function repair(spec: TLayoutSpec, problems: readonly Problem[]): TLayoutSpec {
  const next = structuredClone(spec) as TLayoutSpec;

  const at = (path: string): unknown => {
    const parts = path.split('/').filter(Boolean);
    let cur: unknown = next;
    for (const p of parts) {
      if (cur === null || typeof cur !== 'object') return undefined;
      cur = (cur as Record<string, unknown>)[p];
    }
    return cur;
  };

  for (const p of problems) {
    switch (p.code) {
      case 'write-without-mutation': {
        // The generator asked for an editable binding and forgot to say what it
        // writes. Downgrade to read rather than guessing a mutation — a wrong
        // guess here is data loss, and read-only is always safe.
        const b = at(p.path) as { direction?: string; mutation?: string } | undefined;
        if (b) { b.direction = 'read'; delete b.mutation; }
        break;
      }
      case 'mutation-without-write': {
        const b = at(p.path) as { mutation?: string } | undefined;
        if (b) delete b.mutation;
        break;
      }
      case 'subgroup-without-group': {
        const q = at(p.path) as { subGroupBy?: unknown } | undefined;
        if (q) delete q.subGroupBy;
        break;
      }
      case 'undeclared-view-state':
      case 'predicate-missing-value': {
        // A filter that cannot resolve is worse than no filter: it silently
        // shows the wrong set. Drop it and let the user re-add one.
        const parent = p.path.replace(/\/where$/, '');
        const holder = at(parent.replace(/\/filter.*$/, '')) as
          { filter?: unknown } | undefined;
        if (holder) delete holder.filter;
        break;
      }
      case 'aggregation-kind-mismatch': {
        const q = at(p.path) as { aggregate?: unknown[] } | undefined;
        if (q?.aggregate) delete (q as { aggregate?: unknown[] }).aggregate;
        break;
      }
      default:
        // Anything else is not mechanically fixable. Leave it and let
        // validation refuse the spec.
        break;
    }
  }
  return next;
}

export async function compile(
  req: GenerationRequest,
  generator: Generator,
  opts: CompileOptions = {},
): Promise<GenerationOutcome> {
  const maxAttempts = opts.maxAttempts ?? 3;
  const priors = (req.poolPriors ?? []).map((p) => p.genomeId);
  let attempts = 0;
  let lastProblems: readonly Problem[] = [];

  for (const gen of [generator, ...(opts.fallback ? [opts.fallback] : [])]) {
    let spec: TLayoutSpec;
    try {
      spec = await gen.propose(req);
    } catch (e) {
      /**
       * A refused revision does NOT fall through to the fallback.
       *
       * Decision `0045` rejects *"silently fall back when accepted structure is
       * incompatible"* — *"a fresh baseline may be useful, but presenting it as
       * a revision hides the loss of a person's arrangement."* The fallback
       * generator is exactly that fresh baseline, so continuing here would
       * reintroduce the loss one layer above the generator that refused it,
       * which is why the refusal is its own class rather than a recognisable
       * message.
       *
       * The caller's recourse is the one the decision names: request again,
       * explicitly, without `accepted` — after showing the refusal.
       */
      if (e instanceof AcceptedNoLongerLegal) {
        return {
          ok: false,
          reason: 'the accepted arrangement is no longer legal for this shape',
          problems: e.problems,
        };
      }
      // A template the host cannot draw is the same kind of refusal — 0055
      // control 1: *"generation refuses in words; it must not quietly emit
      // Morph's generic table under app-like colours"* — and the fallback
      // generator is exactly that generic table.
      if (e instanceof TemplateUnsatisfiable) {
        return {
          ok: false,
          reason: `${e.template} cannot be produced in this runtime`,
          problems: e.problems,
        };
      }
      lastProblems = [{ path: '/', code: 'generator-threw', message: String(e) }];
      continue;
    }

    for (let round = 0; round < maxAttempts; round += 1) {
      attempts += 1;
      const result = validateSpec(spec);
      if (result.valid) {
        // A VALID spec is not yet an ACCEPTED one when a template was
        // asked for: every generator's result is held to the template's
        // contract, not only the baseline's own builder (@Codex, P2 on
        // d0c42da). A shortfall is a refusal, never a fallback.
        if (req.template !== undefined) {
          const short = templateShortfalls(spec, req.template);
          if (short.length) {
            return {
              ok: false,
              reason: `${gen.name} did not produce ${req.template}`,
              problems: short,
            };
          }
        }
        return {
          ok: true,
          spec,
          provenance: { generator: gen.name, priors, attempts },
        };
      }
      lastProblems = result.problems;
      const repaired = repair(spec, result.problems);
      // No progress means repair does not know how to fix this. Trying again
      // would just burn rounds.
      if (JSON.stringify(repaired) === JSON.stringify(spec)) break;
      spec = repaired;
    }
  }

  return {
    ok: false,
    reason: 'no generator produced a valid spec',
    problems: lastProblems.map((p) => `${p.path}: ${p.code} — ${p.message}`),
  };
}
